import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Layout } from "components/Layout";
import { Button } from "components/Form/Button";
import { useNavigate, useLocation } from "react-router-dom";
import { AppRoutes, CaptureType, isQrCaptureType } from "types";
import {
  StepCaptureData,
  useMultiStepCapture,
} from "contexts/MultiStepCaptureContext";
import {
  UploadContext,
  UploadFileRequest,
  UploadType,
} from "contexts/UploadContext";
import { StepMetadata } from "lib/api";
import { GalleryPreviewLabels } from "components/GalleryPreview/Labels";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { SaveProgressModal } from "components/SaveProgressModal";
import { toast } from "react-toastify";
import { useTaskDefinition } from "hooks/useTaskDefinition";
import {
  getStepSchemaFingerprint,
  getTaskStepDetailFields,
  getTaskSteps,
} from "lib/task-steps";
import {
  getStoredWorkTaskSharedDetails,
  getWorkTaskStepRoute,
  getWorkTaskStepRouteState,
  isStoredCaptureForWorkTaskStep,
  isWorkTaskStepComplete,
} from "lib/work-task-navigation";
import { buildCaptureCollectionDetails } from "lib/get-collection-details";
import {
  getCompletedTaskSteps,
  saveCompletedTaskSteps,
} from "lib/task-progress";

// Helper function to determine upload type based on capture type
const getUploadTypeFromCaptureType = (captureType?: string): UploadType => {
  switch (captureType) {
    case "iris":
      return UploadType.IRIS;
    case "pcr":
      return UploadType.PCR;
    case "photo":
    case "file":
    case "video":
    default:
      return UploadType.REGULAR;
  }
};

const getReviewUnitLabel = (
  captureType: CaptureType | undefined,
  count: number
): string => {
  const plural = count === 1 ? "" : "s";

  switch (captureType) {
    case "iris":
      return `iris image${plural}`;
    case "photo":
      return `image${plural}`;
    case "file":
      return `file upload${plural}`;
    case "video":
      return `video${plural}`;
    case "pcr":
      return `PCR upload${plural}`;
    default:
      return `upload${plural}`;
  }
};

export function MultiStepUpload() {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasDuplicates, setHasDuplicates] = useState<boolean>(false);
  const {
    stepCaptures,
    addStepCapture,
    getAllFiles,
    clearAllCaptures,
    getStepDetails,
    getStepDetailsWithFields,
    stepDetails,
    currentTaskId,
    setCurrentTaskId,
    isLoadingSteps,
  } = useMultiStepCapture();
  const { upload, uploadBatch, setFiles, resetFiles, checkForDuplicates } =
    useContext(UploadContext);
  const { config, contributor } = useContext(CollectionDetailsContext);

  const taskId = location.state?.taskId;
  const taskName = location.state?.taskName;
  const captureType = location.state?.captureType;
  const workTaskDocumentId = taskId
    ? config?.tasks[taskId]?.workTaskDocumentId
    : undefined;
  const {
    instructions,
    workTask: configuredWorkTask,
    orbTask,
    loading: workTaskLoading,
    error: taskLoadError,
  } = useTaskDefinition({
    taskId,
    workTaskDocumentId,
    enabled: config !== undefined,
  });
  const workTask = configuredWorkTask || orbTask;
  const workTaskError = Boolean(taskLoadError);
  const storedSharedDetails = useMemo(
    () =>
      workTask ? getStoredWorkTaskSharedDetails(workTask, stepCaptures) : null,
    [stepCaptures, workTask]
  );
  const lockedStepNumbers = new Set(
    stepCaptures
      .filter(
        (capture) => capture.uploadCaptureId || capture.uploadedFileKeys?.length
      )
      .map((capture) => capture.stepNumber)
  );

  // Set taskId in context when component mounts
  useEffect(() => {
    if (taskId) {
      setCurrentTaskId(taskId);
    }
  }, [taskId, setCurrentTaskId]);

  // Set accumulated files into UploadContext when component mounts
  useEffect(() => {
    const allFiles = getAllFiles();
    const defaultCaptureType = captureType as CaptureType | undefined;
    const fileCaptureTypes = stepCaptures.flatMap((step) =>
      step.files.map(() => step.captureType ?? defaultCaptureType)
    );
    setFiles(allFiles, fileCaptureTypes);

    // Cleanup function to reset UploadContext files when leaving this page
    return () => {
      resetFiles();
    };
  }, [captureType, getAllFiles, resetFiles, setFiles, stepCaptures]);

  // Check for duplicates when files change (preview screen only)
  useEffect(() => {
    const allFiles = getAllFiles();
    if (allFiles.length > 0) {
      const duplicates = checkForDuplicates();
      const hasDups = duplicates.length > 0;
      setHasDuplicates(hasDups);
    } else {
      setHasDuplicates(false);
    }
  }, [getAllFiles, checkForDuplicates]);

  const reviewSteps = useMemo(
    () =>
      workTask
        ? workTask.steps.map((step) => {
            const stored = stepCaptures.find((capture) =>
              isStoredCaptureForWorkTaskStep(step, capture)
            );
            return (
              stored || {
                stepNumber: step.stepNumber,
                stepLabel: step.stepLabel,
                stepTitle: step.stepTitle,
                numberOfCaptures: step.numberOfCaptures,
                captureTaskId: step.captureTaskId,
                captureTaskStepNumber: step.captureTaskStepNumber,
                captureType: step.captureType,
                files: [],
                isComplete: false,
              }
            );
          })
        : stepCaptures.filter((capture) => capture.stepNumber > 0),
    [stepCaptures, workTask]
  );

  const isReviewStepComplete = useCallback(
    (stepNumber: number) => {
      if (!workTask) {
        const capture = stepCaptures.find(
          (step) => step.stepNumber === stepNumber
        );
        const currentStep = getTaskSteps(instructions).find(
          (step) => step.stepNumber === stepNumber
        );
        return Boolean(
          capture &&
            currentStep &&
            capture.schemaFingerprint ===
              getStepSchemaFingerprint({
                captureType: captureType as CaptureType | undefined,
                numberOfCaptures: currentStep.numberOfCaptures,
                fields: getTaskStepDetailFields(instructions, stepNumber),
                stepLabel: currentStep.configuredStepLabel
                  ? currentStep.stepLabel
                  : undefined,
              }) &&
            (capture.isComplete ??
              capture.files.length >= capture.numberOfCaptures)
        );
      }
      const step = workTask.steps.find(
        (candidate) => candidate.stepNumber === stepNumber
      );
      return step
        ? isWorkTaskStepComplete(
            step,
            stepCaptures,
            stepDetails,
            storedSharedDetails
          )
        : false;
    },
    [
      captureType,
      instructions,
      stepCaptures,
      stepDetails,
      storedSharedDetails,
      workTask,
    ]
  );

  const handleUploadAll = useCallback(async () => {
    if (!taskId) return;

    let succeeded = false;
    if (workTask) {
      const captureIdsByTask = new Map<number, string>();
      const captureTasksById = new Map<string, number>();
      for (const stepCapture of stepCaptures) {
        const workStep = workTask.steps.find(
          (step) => step.stepNumber === stepCapture.stepNumber
        );
        if (!workStep || isQrCaptureType(workStep.captureType)) continue;
        const existingCaptureId = stepCapture.uploadCaptureId;
        const groupCaptureId = captureIdsByTask.get(workStep.captureTaskIndex);
        if (
          existingCaptureId &&
          groupCaptureId &&
          existingCaptureId !== groupCaptureId
        ) {
          toast.error(
            `Saved upload progress for ${workStep.captureTaskName} has conflicting capture IDs. Do not retry; contact support.`
          );
          return;
        }
        if (existingCaptureId) {
          const existingTaskIndex = captureTasksById.get(existingCaptureId);
          if (
            existingTaskIndex !== undefined &&
            existingTaskIndex !== workStep.captureTaskIndex
          ) {
            toast.error(
              "Saved progress merges different Capture Tasks into one capture ID. Do not retry; contact support."
            );
            return;
          }
          captureIdsByTask.set(workStep.captureTaskIndex, existingCaptureId);
          captureTasksById.set(existingCaptureId, workStep.captureTaskIndex);
        }
      }

      const requests: UploadFileRequest[] = stepCaptures.flatMap(
        (stepCapture) => {
          const workStep = workTask.steps.find(
            (step) => step.stepNumber === stepCapture.stepNumber
          );
          if (
            !workStep ||
            isQrCaptureType(workStep.captureType) ||
            !isStoredCaptureForWorkTaskStep(workStep, stepCapture)
          ) {
            return [];
          }

          const firstCaptureStep = workTask.steps.find(
            (step) => step.captureTaskIndex === workStep.captureTaskIndex
          );
          const captureDetails = buildCaptureCollectionDetails({
            fields: workStep.captureCollectionDetailFields,
            sharedDetails: storedSharedDetails,
            captureDetails: firstCaptureStep
              ? getStepDetailsWithFields(firstCaptureStep.stepNumber) || []
              : [],
            requiredFields: workStep.requiredAdditionalDataFields,
            allRequiredFields: workTask.requiredAdditionalDataFields,
          });
          const stepSpecificDetails = (
            getStepDetails(stepCapture.stepNumber) || []
          ).filter((detail) => {
            const separator = detail.indexOf(":");
            const labelId = (
              separator === -1 ? detail : detail.slice(0, separator)
            ).trim();
            return workStep.stepDetailFields.some(
              (field) => field.label_id?.toString().trim() === labelId
            );
          });
          const metadata: StepMetadata | undefined =
            workStep.hasCaptureTaskSteps
              ? {
                  step_number: workStep.captureTaskStepNumber,
                  step_label: workStep.captureTaskStepLabel,
                  step_title: workStep.captureTaskStepTitle,
                  number_of_captures: workStep.numberOfCaptures,
                  step_details:
                    stepSpecificDetails.length > 0
                      ? stepSpecificDetails
                      : undefined,
                }
              : undefined;

          const uploadedFileKeys = new Set(stepCapture.uploadedFileKeys || []);
          return stepCapture.files.flatMap((file) => {
            const fileKey = [
              file.name,
              file.size,
              file.type,
              file.lastModified,
            ].join(":");
            if (uploadedFileKeys.has(fileKey)) return [];

            return [
              {
                file,
                type: getUploadTypeFromCaptureType(workStep.captureType),
                taskId: workStep.captureTaskId,
                captureGroupId: String(workStep.captureTaskIndex),
                existingCaptureId: captureIdsByTask.get(
                  workStep.captureTaskIndex
                ),
                onCaptureId: async (captureId: string) => {
                  for (const groupCapture of stepCaptures) {
                    const groupStep = workTask.steps.find(
                      (step) => step.stepNumber === groupCapture.stepNumber
                    );
                    if (
                      !groupStep ||
                      groupStep.captureTaskIndex !==
                        workStep.captureTaskIndex ||
                      groupCapture.uploadCaptureId === captureId
                    ) {
                      continue;
                    }
                    await addStepCapture({
                      ...groupCapture,
                      uploadCaptureId: captureId,
                    });
                  }
                },
                captureCollectionDetails: captureDetails,
                sessionMetadata: stepCapture.sessionMetadata || {},
                stepMetadata: metadata,
                onSuccess: async (captureId: string) => {
                  const nextUploadedFileKeys = [
                    ...Array.from(uploadedFileKeys),
                    fileKey,
                  ];
                  await addStepCapture({
                    ...stepCapture,
                    uploadCaptureId: captureId,
                    uploadedFileKeys: nextUploadedFileKeys,
                  });
                  uploadedFileKeys.add(fileKey);
                },
              },
            ];
          });
        }
      );
      succeeded = await uploadBatch(
        requests,
        Array.from(new Set(captureIdsByTask.values()))
      );
    } else {
      const uploadType = getUploadTypeFromCaptureType(captureType);
      const stepMetadataMap = new Map<File, StepMetadata>();
      const sessionMetadataMap = new Map<File, Record<string, string>>();

      stepCaptures.forEach((stepCapture) => {
        const metadata: StepMetadata = {
          step_number: stepCapture.stepNumber,
          step_label: stepCapture.stepLabel,
          step_title: stepCapture.stepTitle,
          number_of_captures: stepCapture.numberOfCaptures,
          step_details: getStepDetails(stepCapture.stepNumber),
        };
        stepCapture.files.forEach((file) => {
          stepMetadataMap.set(file, metadata);
          sessionMetadataMap.set(file, stepCapture.sessionMetadata || {});
        });
      });
      succeeded = await upload(
        uploadType,
        taskId,
        stepMetadataMap,
        sessionMetadataMap
      );
    }

    if (!succeeded) return;

    // Clear accumulated captures after successful upload.
    try {
      await saveCompletedTaskSteps(
        taskId,
        contributor,
        getCompletedTaskSteps({
          flow: workTask,
          instructions,
          captureType,
          captures: stepCaptures,
          details: stepDetails,
        })
      );
      await clearAllCaptures(true);
    } catch (error) {
      console.error(
        "Upload succeeded but local progress cleanup failed:",
        error
      );
      toast.warn(
        "Upload completed, but saved local progress could not be cleared."
      );
    }
  }, [
    taskId,
    contributor,
    instructions,
    stepDetails,
    captureType,
    addStepCapture,
    upload,
    clearAllCaptures,
    stepCaptures,
    getStepDetails,
    getStepDetailsWithFields,
    storedSharedDetails,
    uploadBatch,
    workTask,
  ]);

  const handleBack = useCallback(() => {
    // Show save progress modal since user has data to potentially save
    setShowSaveModal(true);
  }, []);

  const handleSaveProgress = useCallback(() => {
    setShowSaveModal(false);
    // Keep data in IndexedDB and navigate to homepage
    navigate(AppRoutes.home);
  }, [navigate]);

  const handleDiscardProgress = useCallback(async () => {
    setProgressActionPending(true);
    try {
      // Clear all captures and collection details from IndexedDB before leaving.
      await clearAllCaptures();
      setShowSaveModal(false);
      navigate(AppRoutes.home);
    } catch (error) {
      console.error("Failed to delete task progress:", error);
      toast.error("Your progress could not be deleted. Please try again.");
    } finally {
      setProgressActionPending(false);
    }
  }, [clearAllCaptures, navigate]);

  const handleStepAction = useCallback(
    async (stepCapture: StepCaptureData) => {
      if (workTask) {
        const workStep = workTask.steps.find(
          (step) => step.stepNumber === stepCapture.stepNumber
        );
        if (!workStep) return;

        navigate(getWorkTaskStepRoute(workStep), {
          state: {
            ...getWorkTaskStepRouteState(workTask, workStep, true),
            redoMode: isQrCaptureType(workStep.captureType),
          },
        });
        return;
      }

      // Keep the saved capture until the user explicitly saves a replacement.
      navigate(AppRoutes.stepCapture, {
        state: {
          stepNumber: stepCapture.stepNumber,
          stepLabel: stepCapture.stepLabel,
          stepTitle: stepCapture.stepTitle,
          numberOfCaptures: stepCapture.numberOfCaptures,
          taskId,
          taskName,
          totalSteps: reviewSteps.length,
          captureType,
          editMode: true, // Flag to indicate we're editing from preview
        },
      });
    },
    [captureType, navigate, reviewSteps.length, taskId, taskName, workTask]
  );

  const handleEditCollectionDetails = useCallback(() => {
    // Navigate to TaskInstructions to show collection details form
    navigate(AppRoutes.taskInstructions, {
      state: {
        taskId,
        taskName,
        nextRoute: AppRoutes.multiStepUpload, // Return to review page after editing
        captureType,
        editCollectionDetails: true, // Flag to show collection details form
      },
    });
  }, [navigate, taskId, taskName, captureType]);

  const totalFiles = getAllFiles().length;
  const duplicatesCount = hasDuplicates ? checkForDuplicates().length : 0;
  const uniqueFilesCount = totalFiles - duplicatesCount;
  const allStepsComplete =
    reviewSteps.length > 0 &&
    reviewSteps.every((step) => isReviewStepComplete(step.stepNumber));
  const sharedWorkTaskLabels = workTask
    ? workTask.sharedCollectionDetailFields.map((field) => {
        const id = field.label_id?.toString().trim() || "";
        const labels = (storedSharedDetails as any)?.labels || {};
        return {
          id,
          title: field.label_title?.toString().trim() || id,
          value: labels[id],
        };
      })
    : [];
  const hasReviewCollectionDetails = workTask
    ? Boolean(
        workTask.sharedCollectionDetailFields.length ||
          config?.labels?.devices.length
      )
    : Boolean(
        config?.labels &&
          Object.keys(config.labels).some((key) => key !== "devices")
      );
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [progressActionPending, setProgressActionPending] = useState(false);

  if (!taskId) {
    return (
      <Layout className="space-y-8">
        <div className="space-y-4 text-center">
          <h1 className="text-24 font-semibold">Review unavailable</h1>
          <p className="text-858494">
            Open the task from the homepage to review its saved captures.
          </p>
          <Button to={AppRoutes.home}>Back to Homepage</Button>
        </div>
      </Layout>
    );
  }

  if (
    workTaskLoading ||
    isLoadingSteps ||
    config === undefined ||
    currentTaskId !== taskId
  ) {
    return (
      <Layout className="space-y-8">
        <div className="flex min-h-[240px] items-center justify-center">
          <p className="text-858494">Loading final review...</p>
        </div>
      </Layout>
    );
  }

  if (workTaskError || (workTaskDocumentId && !workTask)) {
    return (
      <Layout className="space-y-8">
        <div className="space-y-4 text-center">
          <h1 className="text-24 font-semibold">Review unavailable</h1>
          <p className="text-858494">
            We could not load this work task. Your saved progress has not
            changed.
          </p>
          <Button to={AppRoutes.home}>Back to Homepage</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      className="space-y-8"
      toolbarClassName="z-[100] !py-3"
      toolbar={
        <>
          <Button
            onClick={handleUploadAll}
            disabled={totalFiles === 0 || hasDuplicates || !allStepsComplete}
          >
            Upload All ({totalFiles} files)
          </Button>
          <button
            onClick={handleBack}
            className="text-sm text-858494 underline hover:text-191c20 transition-colors"
          >
            Back to Homepage
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="text-center space-y-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {totalFiles > 0 ? "Review All Captures" : "No Captures Found"}
            </h1>
            <p className="text-lg text-gray-600 mt-2">
              {totalFiles > 0
                ? "Verify your inputs and captures before uploading"
                : "No captures found. Go back to capture some files."}
            </p>
          </div>

          {totalFiles > 0 && !hasDuplicates && (
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-green-800 font-semibold">
                  {totalFiles} files ready
                </span>
              </div>
              <div className="h-4 w-px bg-green-300"></div>
              <div className="text-green-700 text-sm">
                {reviewSteps.length} step{reviewSteps.length !== 1 ? "s" : ""}{" "}
                completed
              </div>
            </div>
          )}

          {totalFiles > 0 && hasDuplicates && (
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <span className="text-red-800 font-semibold">
                  {uniqueFilesCount} files ready
                </span>
              </div>
              <div className="h-4 w-px bg-red-300"></div>
              <div className="text-red-700 text-sm">duplicates found</div>
            </div>
          )}
        </div>

        {reviewSteps.length === 0 && (
          <div className="text-center space-y-4">
            <p className="text-858494">No captures found.</p>
            <Button onClick={handleBack}>Back to Homepage</Button>
          </div>
        )}

        {reviewSteps.map((stepCapture) => {
          const resolvedStep = workTask?.steps.find(
            (step) => step.stepNumber === stepCapture.stepNumber
          );
          const reviewCaptureType =
            resolvedStep?.captureType ?? stepCapture.captureType ?? captureType;
          const isQrStep = isQrCaptureType(reviewCaptureType);
          const expectedFileCount =
            resolvedStep?.maximumNumberOfCaptures === undefined
              ? stepCapture.files.length
              : stepCapture.numberOfCaptures;
          const reviewUnitLabel = getReviewUnitLabel(
            reviewCaptureType as CaptureType | undefined,
            expectedFileCount
          );
          const stepComplete = isReviewStepComplete(stepCapture.stepNumber);
          const stepHasUploadedFiles = lockedStepNumbers.has(
            stepCapture.stepNumber
          );
          const userStepDetails = getStepDetailsWithFields(
            stepCapture.stepNumber
          );

          // Check if any of this step's files are duplicates
          const stepHasDuplicates =
            hasDuplicates &&
            stepCapture.files.some((file) =>
              checkForDuplicates().some(
                (dupFile) =>
                  dupFile.name === file.name &&
                  dupFile.size === file.size &&
                  dupFile.type === file.type
              )
            );

          return (
            <div
              key={stepCapture.stepNumber}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
            >
              {/* Step Header */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="w-8 h-8 bg-3461fd text-white rounded-lg flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      {stepCapture.stepNumber}
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-gray-900">
                        {stepCapture.stepTitle}
                      </h3>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Step {stepCapture.stepNumber} of {reviewSteps.length}
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-xs text-3461fd font-semibold">
                      {isQrStep
                        ? reviewCaptureType === "rtms"
                          ? "RTMS capture"
                          : "Orb capture"
                        : resolvedStep?.maximumNumberOfCaptures === undefined
                        ? `${stepCapture.files.length} ${reviewUnitLabel}`
                        : `${stepCapture.files.length} / ${stepCapture.numberOfCaptures} ${reviewUnitLabel}`}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {stepComplete ? "Complete" : "In Progress"}
                    </div>
                  </div>
                </div>
              </div>

              {/* User Input Details - Most Prominent Section */}
              {userStepDetails && userStepDetails.length > 0 && (
                <div className="px-4 py-3 bg-blue-50 border-b border-gray-100">
                  <div className="space-y-3">
                    <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
                      Your Details:
                    </span>
                    {userStepDetails.map((detail, index) => (
                      <div key={index} className="space-y-1.5">
                        <div className="text-xs font-medium text-blue-600">
                          {detail.label_title}:
                        </div>
                        <div className="w-full break-words rounded border border-blue-200 bg-white px-2 py-1 text-sm font-semibold text-gray-900">
                          {detail.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Images Section */}
              {!isQrStep && stepCapture.files.length > 0 && (
                <div className="px-4 py-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {stepCapture.files.map((file, fileIndex) => (
                      <div
                        key={fileIndex}
                        className="aspect-square rounded-lg overflow-hidden bg-gray-100 shadow-sm border border-gray-200"
                      >
                        {file.type.startsWith("video/") ? (
                          <video
                            src={URL.createObjectURL(file)}
                            className="w-full h-full object-cover"
                            controls
                          />
                        ) : file.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Step ${stepCapture.stepNumber} - Capture ${
                              fileIndex + 1
                            }`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center p-3 text-center text-sm text-gray-700">
                            {file.name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!isQrStep && stepCapture.files.length === 0 && (
                <div className="px-4 py-3 text-center">
                  <p className="text-sm text-gray-500">
                    No captures yet - click edit to add
                  </p>
                </div>
              )}

              {/* Action Section */}
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      stepHasDuplicates
                        ? "bg-red-100 text-red-700"
                        : stepComplete
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {stepHasDuplicates
                      ? "[Duplicate]"
                      : stepComplete
                      ? "Ready"
                      : "Incomplete"}
                  </div>
                  <button
                    onClick={() => handleStepAction(stepCapture)}
                    disabled={stepHasUploadedFiles}
                    className="flex items-center gap-1 px-3 py-1.5 bg-3461fd text-white rounded-lg hover:bg-3461fd/90 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {!isQrStep && (
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    )}
                    {isQrStep ? "Redo" : "Edit"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {totalFiles > 0 && hasReviewCollectionDetails && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {/* Collection Details Header */}
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-3461fd text-white rounded-lg flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      Collection Details
                    </h3>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Task-level information
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleEditCollectionDetails}
                  disabled={lockedStepNumbers.size > 0}
                  className="flex items-center gap-1 px-3 py-1.5 bg-3461fd text-white rounded-lg hover:bg-3461fd/90 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Edit
                </button>
              </div>
            </div>

            {/* Collection Details Content */}
            <div className="px-4 py-4">
              {workTask ? (
                <div className="grid gap-2.5">
                  {config?.labels?.devices.length ? (
                    <div className="text-sm text-858494">
                      <span className="font-medium">Device:</span>{" "}
                      {storedSharedDetails?.device || "Not selected"}
                    </div>
                  ) : null}
                  {sharedWorkTaskLabels.map((label) => (
                    <div key={label.id} className="text-sm text-858494">
                      <span className="font-medium">{label.title}:</span>{" "}
                      {label.value || "Not set"}
                    </div>
                  ))}
                </div>
              ) : (
                <GalleryPreviewLabels readOnly />
              )}
            </div>
          </div>
        )}

        {totalFiles > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6">
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-2">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <h3 className="text-xl font-bold text-blue-900">
                  Ready for Upload
                </h3>
              </div>
              <div className="space-y-2">
                <p className="text-lg font-semibold text-blue-800">
                  {totalFiles} files • {reviewSteps.length} step
                  {reviewSteps.length !== 1 ? "s" : ""}
                </p>
                <p className="text-sm text-blue-700">
                  All files will be uploaded with their respective step
                  information and user inputs
                </p>
              </div>
              <div className="pt-2">
                <div className="flex items-center justify-center gap-4 text-xs text-blue-600">
                  <div className="flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Images captured
                  </div>
                  <div className="flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Details verified
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <SaveProgressModal
        show={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveProgress}
        onDiscard={handleDiscardProgress}
        busy={progressActionPending}
      />
    </Layout>
  );
}
